import { ILevelVF, IViewFolder, ViewCategory } from '../../interfaces/CustomViewFolder';
import { cloneDeep } from 'lodash-es';
import { ManageViewConstants } from '../ManageViewConstants';

export const generateTreeView = (vnF: IViewFolder[], searchTerm: string): IViewFolder[] => {
  const treeView: IViewFolder[] = []; // Things without parent
  const levelViewFolders: ILevelVF[] = [];

  const viewFolders = cloneDeep(vnF);

  const clonedViewFolder = cloneDeep(viewFolders);

  const favoritesViews: IViewFolder[] = [];
  const defaultView: IViewFolder[] = [];

  clonedViewFolder.forEach(({ ...obj }) => {
    const lvl: number = obj.category === 0 ? 0 : -1;
    levelViewFolders.push(Object.assign(obj, { level: lvl }) as ILevelVF);
    if (obj.isFavorite) {
      favoritesViews.push(obj);
    }
    if (obj.isDefault) {
      defaultView.push(obj);
    }
  });

  // make them accessible by guid on this map
  const allViewFolders = {};
  const viewFolderObj = {};

  viewFolders.forEach((item) => {
    allViewFolders[item.id] = item;
  });

  levelViewFolders.forEach((item) => {
    viewFolderObj[item.id] = item;
  });

  // connect children to its parent, and split roots apart
  Object.keys(allViewFolders).forEach((guid) => {
    const item: IViewFolder = allViewFolders[guid];
    const levelItem: ILevelVF = viewFolderObj[guid];
    if (item.parentId === null) {
      treeView.push(item);
    } else if (item.parentId in allViewFolders) {
      const p = allViewFolders[item.parentId];

      // Only allow User views and sub-folders under "My Views" folders
      if (
        p.name === ManageViewConstants.generateTreeView.customUserViewsFolderName &&
        p.category === ViewCategory.Folder &&
        item.category !== ViewCategory.Folder &&
        item.category !== ViewCategory.UserView
      ) {
        return;
      }

      if (levelItem.category === 0)
        levelItem.level = viewFolderObj[levelItem.parentId as string].level + 1;

      if (!('children' in p)) {
        p.children = [];
      }
      if (p.children === null) {
        p.children = [];
      }
      p.children.push(item);
    }
  });

  const customFavViewsFolder = {
    id: ManageViewConstants.generateTreeView.customFavViewsFolderId,
    name: ManageViewConstants.generateTreeView.customFavViewsFolderName,
    parentId: null,
    category: ViewCategory.Folder,
    children: favoritesViews,
    isFavorite: false,
    isDefault: false,

    createdOn: '',
    level: 0,
    temporaryViewID: null,
  };

  const customDefaultViewFolder = {
    id: ManageViewConstants.generateTreeView.customDefaultViewFolderId,
    name: ManageViewConstants.generateTreeView.customDefaultViewFolderName,
    parentId: null,
    category: ViewCategory.Folder,
    children: defaultView,
    isFavorite: false,
    isDefault: false,
    createdOn: '',
    level: 0,
    temporaryViewID: null,
  };

  const namesToShift = [
    ManageViewConstants.generateTreeView.customSharedWithMeViewsFolderName,
    ManageViewConstants.generateTreeView.customSystemViewFolderName,
    ManageViewConstants.generateTreeView.customSharedViewsFolderName,
    ManageViewConstants.generateTreeView.customUserViewsFolderName,
  ];

  const elementsToShift = [];

  for (const name of namesToShift) {
    const index = treeView.findIndex((node) => node.name === name);
    if (index !== -1) {
      const [element] = treeView.splice(index, 1);
      elementsToShift.push({
        ...element,
        parentId: null,
      });
    } else {
      elementsToShift.push({
        id: name,
        name: name,
        parentId: null,
        category: ViewCategory.Folder,
        children: [],
        isFavorite: false,
        isDefault: false,
        createdOn: '',
        level: 0,
        temporaryViewID: null,
      });
    }
  }

  treeView.unshift(...elementsToShift);

  if (treeView) {
    treeView.unshift(customFavViewsFolder);
    treeView.unshift(customDefaultViewFolder);
  }

  if (searchTerm) {
    const checkChildrenForTestKeyword = (node: IViewFolder): boolean => {
      if (node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      if (node.children) {
        const filteredChildren = node.children.filter((child) =>
          checkChildrenForTestKeyword(child)
        );
        if (filteredChildren.length > 0) {
          node.children = filteredChildren;
          return true;
        }
      }
      return false;
    };

    const filteredTreeView: IViewFolder[] = [];
    for (const item of treeView) {
      const hasTestKeyword = checkChildrenForTestKeyword(item);
      if (hasTestKeyword) {
        filteredTreeView.push(item);
      }
    }
    // Update the original treeView with the filteredTreeView
    treeView.length = 0;
    Array.prototype.push.apply(treeView, filteredTreeView);
  }
  return treeView;
};

export const getAllBranchValueShouldBeOpen = (
  Folders: IViewFolder[],
  FolderViewId: string | undefined
): string[] => {
  const openItems: string[] = [];

  const findParentId = (parentId: string) => {
    const parent = Folders.find((folder) => folder.id === parentId);
    if (parent) {
      openItems.unshift(parent.id);
      if (parent.parentId) {
        findParentId(parent.parentId);
      }
    }
  };

  const selectedViewParentIdSearch = () => {
    const initialViewParentId = Folders.find((folder) => folder.id === FolderViewId)?.parentId;
    if (initialViewParentId) {
      openItems.unshift(initialViewParentId);
      findParentId(initialViewParentId);
    }
  };

  selectedViewParentIdSearch();

  return openItems;
};

export const getOpenFolderIds = (
  Folders: { [key: string]: IViewFolder },
  FolderViewId: string | undefined
): string[] => {
  const openItems: string[] = [];

  const findParentId = (currentId: string, parentId: string) => {
    const parent: IViewFolder = Folders[parentId];
    if (parent && parent.id !== currentId) {
      openItems.unshift(parent.id);
      if (parent.parentId && parent.parentId !== parent.id) {
        findParentId(parent.id, parent.parentId);
      }
    }
  };

  const selectedViewParentIdSearch = () => {
    if (FolderViewId) {
      const initialViewParentId = Folders[FolderViewId]?.parentId;
      if (initialViewParentId && initialViewParentId !== FolderViewId) {
        openItems.unshift(initialViewParentId);
        findParentId(FolderViewId, initialViewParentId);
      }
    }
  };

  selectedViewParentIdSearch();

  return openItems;
};

export const findParentFoldesNeedsToBeOpen = (
  viewAndFoldersList: IViewFolder[],
  searchTerm: string,
  viewOrFolder: IViewFolder | null
): string[] => {
  const parentOpenedIds: string[] = [];

  const findAncestorParentIds = (node: IViewFolder) => {
    if (node.parentId !== null) {
      if (parentOpenedIds.indexOf(node.parentId) === -1) {
        parentOpenedIds.push(node.parentId);
        //Matched node's parent folder
        const parentNode = viewAndFoldersList.find(({ id }) => id === node.parentId);
        if (parentNode) {
          findAncestorParentIds(parentNode);
        }
      }
    } else {
      //Matched node is top most folder
      if (parentOpenedIds.indexOf(node.id) === -1) {
        parentOpenedIds.push(node.id);
      }
    }
  };

  if (searchTerm === '') {
    viewOrFolder && findAncestorParentIds(viewOrFolder);
  } else {
    viewAndFoldersList.forEach((node) => {
      if (node.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        //Find node's all ancestor parent node ids and push
        findAncestorParentIds(node);
      }
    });
  }

  return parentOpenedIds;
};
// Generated by GitHub Copilot | Updated on: 2026-03-30 | 1 lines modified | Fixed placeholder folders using favoritesViews as children instead of empty array
// Generated by GitHub Copilot | Updated on: 2026-03-30 | 9 lines modified | Filter non-User views from My Views folder in tree
